import type { LinesheetCheckPurpose, ListLinesheetsInput } from "@nexxt/contracts/catalog";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { catalogClient, type LinesheetSummary } from "../client";

// Server state of the linesheets (pattern.md §10): one factory, the same keys for loaders, screens and mutations.

export type { LinesheetOptions, LinesheetSummary } from "../client";
/** The full page reads the same header the list shows. */
export type LinesheetDetail = LinesheetSummary;

/** The filters of the list input (`listLinesheetsInput`), without the paging the queries add. */
export type LinesheetFilters = Pick<
  ListLinesheetsInput,
  "status" | "priceStatus" | "bcSyncStatus" | "joorSyncStatus" | "season" | "brand" | "q"
> & {
  archived?: boolean | undefined;
  pricesStale?: boolean | undefined;
  /** The sets of the app's queues "To do", as a JSON array (ADR-302). */
  anyOf?: string | undefined;
};

const PAGE_SIZE = 50;

const keys = {
  all: ["linesheets"] as const,
  /** Prefixes of every cached list and every cached detail: the mutations patch them all. */
  lists: ["linesheets", "list"] as const,
  details: ["linesheets", "detail"] as const,
  /** Prefix of the checks of one linesheet, for every purpose and version. */
  checksOf: (linesheetId: string) => ["linesheets", "check", linesheetId] as const,
  historyOf: (linesheetId: string) => ["linesheets", "history", linesheetId] as const,
};

export const linesheetQueries = {
  ...keys,

  list: (filters: LinesheetFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        catalogClient().listLinesheets({ ...filters, page: pageParam, pageSize: PAGE_SIZE }, signal),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  detail: (key: string) =>
    queryOptions({
      queryKey: [...keys.details, key],
      queryFn: ({ signal }) => catalogClient().getLinesheet({ key }, signal),
    }),

  /**
   * What an action still needs (checkLinesheet): the screen shows it before the user tries. The version is in the
   * key, so it is asked again after every change of the header; a change of the lines reloads it (line.mutations).
   */
  check: (linesheetId: string, purpose: LinesheetCheckPurpose, version: number) =>
    queryOptions({
      queryKey: [...keys.checksOf(linesheetId), purpose, version],
      queryFn: ({ signal }) => catalogClient().checkLinesheet({ linesheetId, for: purpose }, signal),
    }),

  history: (linesheetId: string) =>
    infiniteQueryOptions({
      queryKey: keys.historyOf(linesheetId),
      queryFn: ({ pageParam, signal }) =>
        catalogClient().getLinesheetHistory({ linesheetId, ...(pageParam && { cursor: pageParam }) }, signal),
      initialPageParam: "",
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    }),

  /** The values of the linesheet form (ADR-048): they change rarely. */
  options: () =>
    queryOptions({
      queryKey: [...keys.all, "options"],
      queryFn: ({ signal }) => catalogClient().getLinesheetOptions({}, signal),
      staleTime: 10 * 60_000,
    }),
};
